import { createApi } from '@reduxjs/toolkit/query/react';

import { URL } from 'api/constants';
import { addParamsToUrl } from 'utils/query';
import { Child, BaseResponse, PaginationResponse } from 'types';
import {
  providesList,
  invalidatesList,
  cacheByIdArgProperty,
  cacheListAndByIdArgProperty,
} from 'utils/query-cache';

import { apiQuery } from '../../query';

import {
  ListResponse,
  UpdateRequest,
  DetailsRequest,
  ChildrenResponse,
  ChildrenListRequest,
  AddToLastCourseSubmissionRequest,
  GetLastCourseSubmissionStatusRequest,
  ChildLastCourseSubmissionStatusResponse,
} from './types';

const lastSubmissionStatusTag = 'CHILD_LAST_SUBMISSION_STATUS';
const tagType = 'CHILDREN';

export const childrenApi = createApi({
  baseQuery: apiQuery,
  reducerPath: 'childrenApi',
  tagTypes: [tagType, lastSubmissionStatusTag],
  endpoints: (build) => ({
    update: build.mutation<Child, UpdateRequest>({
      invalidatesTags: cacheListAndByIdArgProperty(tagType),
      query: ({ id, ...data }) => ({
        data,
        method: 'patch',
        url: URL.CHILDREN_DETAILS.replace(':id', id),
      }),
    }),

    details: build.query<Child, DetailsRequest>({
      providesTags: cacheByIdArgProperty(tagType),
      transformResponse: (response: ChildrenResponse) => response.data,
      query: ({ id }) => ({
        method: 'get',
        url: URL.CHILDREN_DETAILS.replace(':id', id),
      }),
    }),

    list: build.query<PaginationResponse<Child>, ChildrenListRequest>({
      providesTags: providesList(tagType),
      transformResponse: (response: ListResponse) => response.data,
      query: (params) => ({
        method: 'get',
        url: addParamsToUrl(URL.CHILDREN, params),
      }),
    }),

    addToLastCourseSubmission: build.mutation<
      void,
      AddToLastCourseSubmissionRequest
    >({
      invalidatesTags: invalidatesList(lastSubmissionStatusTag),
      query: ({ id }) => ({
        method: 'patch',
        url: URL.CHILDREN_ADD_TO_LAST_COURSE_SUBMISSION.replace(':id', id),
      }),
    }),

    lastCourseSubmissionStatus: build.query<
      ChildLastCourseSubmissionStatusResponse,
      GetLastCourseSubmissionStatusRequest
    >({
      providesTags: () => [{ id: 'LIST', type: lastSubmissionStatusTag }],
      transformResponse: (
        response: BaseResponse<ChildLastCourseSubmissionStatusResponse>
      ) => response.data,
      query: ({ id }) => ({
        method: 'get',
        url: URL.CHILDREN_LAST_COURSE_SUBMISSION_STATUS.replace(':id', id),
      }),
    }),
  }),
});

export const {
  useDetailsQuery,
  useLazyListQuery,
  useUpdateMutation,
  useLazyDetailsQuery,
  useLastCourseSubmissionStatusQuery,
  useAddToLastCourseSubmissionMutation,
} = childrenApi;
